import { Switch } from '@material-ui/core';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { getSimpleSingleOptionSelects } from '~/src/dash/components/common';
import { SimpleSingleSelect } from '~/src/dash/components/dropdowns/SimpleSingleSelect';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacingV2 } from '~/src/dash/components/layout/common';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import {
  ButtonV2,
  Col,
  Cols,
  ISimpleOption,
  Row,
  Rows,
  Text,
  TextButton,
  TextInput,
  usePanel,
} from '~/src/dash/publicaUI';
import { IListItem } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { IOrderBy, OrderByDirections } from '~/src/dash/views/reporting/ReportBuilder/common';

interface IPillProps {
  isSelected: boolean;
}

const DescendingPill = ({ isSelected }: IPillProps) => {
  return (
    <Col displayFlex style={{ alignItems: 'center' }}>
      <IconV2 name='arrow' color={isSelected ? publicaColors.teal : publicaColors.gray} />
      <Text small color={isSelected ? publicaColors.teal : publicaColors.gray}>
        Descending
      </Text>
    </Col>
  );
};

const AscendingPill = ({ isSelected }: IPillProps) => {
  return (
    <Col displayFlex style={{ alignItems: 'center' }}>
      <IconV2
        name='arrow'
        color={isSelected ? publicaColors.teal : publicaColors.gray}
        style={{ transform: 'scaleY(-1)' }}
      />
      <Text small color={isSelected ? publicaColors.teal : publicaColors.gray}>
        Ascending
      </Text>
    </Col>
  );
};

interface ICustomizeDataSectionProps {
  selectedMetrics: IListItem[];
  orderBys: IOrderBy[];
  setOrderBys: (orderBys: IOrderBy[]) => void;
  limit: number;
  setLimit: (input: number) => void;
  isLoading: boolean;
}

export const CustomizeDataSection = ({
  selectedMetrics,
  limit,
  orderBys,
  setOrderBys,
  setLimit,
  isLoading,
}: ICustomizeDataSectionProps) => {
  const [isOrderByActive, setIsOrderByActive] = useState(false);
  const [isOrderByRowExpanded, setIsOrderByRowExpanded] = useState(false);
  const [isLimitRowsActive, setIsLimitRowsActive] = useState(false);
  const [isLimitRowsRowExpanded, setIsLimitRowsRowExpanded] = useState(false);

  const { width } = usePanel();

  useEffect(() => {
    if (!isLoading) {
      unstable_batchedUpdates(() => {
        if (limit > 0) {
          setIsLimitRowsActive(true);
          setIsLimitRowsRowExpanded(true);
        }

        if (orderBys.length > 0) {
          setIsOrderByActive(true);
          setIsOrderByRowExpanded(true);
        }
      });
    }
  }, [isLoading]);

  const getOrderByPills = (direction: OrderByDirections): Array<ISimpleOption> => {
    return [
      {
        key: 'orderByDirection',
        value: 'Ascending',
        label: 'Ascending',
        customComponent: AscendingPill({ isSelected: direction === 'Ascending' }),
      },
      {
        key: 'orderByDirection',
        value: 'Descending',
        label: 'Descending',
        customComponent: DescendingPill({ isSelected: direction === 'Descending' }),
      },
    ];
  };

  const getSelectableOrderBys = (selectedOrderByKey: string) => {
    const orderByKeys = orderBys.map((orderBy) => orderBy.key);
    return selectedMetrics.filter(
      (metric) => !orderByKeys.includes(metric.ID) || metric.ID === selectedOrderByKey,
    );
  };

  const getOrderByText = (orderByDirection: OrderByDirections) => {
    return orderByDirection === 'Descending'
      ? 'Highest value will show first.'
      : 'Lowest value will show first.';
  };

  const handleOrderByActive = () => {
    if (!isOrderByActive) {
      setIsOrderByRowExpanded(true);
    }
    if (orderBys.length === 0) {
      setOrderBys([{ key: '', direction: 'Descending' }]);
    }
    setIsOrderByActive(!isOrderByActive);
  };

  const handleLimitRowsActive = () => {
    if (!isLimitRowsActive) {
      setIsLimitRowsRowExpanded(true);
    }
    setIsLimitRowsActive(!isLimitRowsActive);
  };

  const handleSelectOrderBy = (idx: number) => {
    const handleSelectOrderByInnerFnc = (id: string) => {
      const orderBysCopy = cloneDeep(orderBys);
      orderBysCopy[idx] = { ...orderBysCopy[idx], key: id };
      setOrderBys(orderBysCopy);
    };
    return handleSelectOrderByInnerFnc;
  };

  const handleAddMetric = () => {
    const orderBysCopy = cloneDeep(orderBys);
    orderBysCopy.push({ key: '', direction: 'Descending' });
    setOrderBys(orderBysCopy);
  };

  const handleSwitchOrderByDirection = (idx: number) => {
    const orderBysCopy = cloneDeep(orderBys);
    orderBysCopy[idx].direction =
      orderBysCopy[idx].direction === 'Descending' ? 'Ascending' : 'Descending';
    setOrderBys(orderBysCopy);
  };

  const handleRemoveOrderBy = (idx: number) => {
    const orderBysCopy = cloneDeep(orderBys);
    orderBysCopy.splice(idx, 1);
    setOrderBys(orderBysCopy);
  };

  return (
    <Rows>
      <Row>
        <Cols gap={spacingV2.medium}>
          <Col width='1rem' paddingTop='0.3rem'>
            <IconV2 name='customize' />
          </Col>
          <Col>
            <Text sizeV2 bold large>
              Customize Data
            </Text>
          </Col>
        </Cols>
      </Row>

      <Rows
        style={{
          minWidth: '27rem',
          maxWidth: '80%',
          padding: `${spacingV2.extraSmall} 3rem`,
        }}
      >
        <Row
          smallVerticalPaddingV2
          extraLargeHorizontalPaddingV2
          displayFlex
          style={{
            justifyContent: 'space-between',
            border: `1px solid ${publicaColors.lightGray}`,
            alignItems: 'center',
          }}
        >
          <Cols
            gap={spacingV2.medium}
            style={{
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Col style={{ width: '8.875rem' }}>
              <Cols gap={spacingV2.medium} style={{ alignItems: 'center' }}>
                <Col>
                  <Switch
                    checked={isOrderByActive}
                    onChange={handleOrderByActive}
                    color='secondary'
                    size='small'
                  />
                </Col>
                <Col>
                  <Text small bold>
                    Order By
                  </Text>
                </Col>
              </Cols>
            </Col>
            {width && width > 1000 && (
              <Col largeHorizontalPaddingV2>
                <Text small>
                  Use to sort report based on any metric selected, ascending or descending.{' '}
                </Text>
              </Col>
            )}
            <Col style={{ marginLeft: 'auto' }}>
              <ButtonV2
                clearBackground
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                }}
                onClick={() => setIsOrderByRowExpanded(!isOrderByRowExpanded)}
              >
                <IconV2
                  name='caret'
                  color={publicaColors.black}
                  style={{ transform: isOrderByRowExpanded ? 'scaleY(-1)' : 'none' }}
                />
              </ButtonV2>
            </Col>
          </Cols>
        </Row>

        {isOrderByRowExpanded && selectedMetrics.length > 0 && (
          <Rows
            style={{
              padding: `${spacingV2.base} calc(${spacingV2.huge} + ${spacingV2.large})`,
              paddingTop: 0,
              border: `1px solid ${publicaColors.lightGray}`,
              borderTop: 'none',
            }}
          >
            {orderBys.map((orderBy, idx) => {
              return (
                <Row noPadding>
                  <Cols wrap style={{ alignItems: 'center' }}>
                    <Col paddingTop={spacingV2.base} marginRight={spacingV2.huge}>
                      <SimpleSingleSelect
                        inputWidth='12.5rem'
                        popoverWidth='12.5rem'
                        options={getSelectableOrderBys(orderBy.key).map((metric) => {
                          return {
                            key: metric.ID,
                            label: metric.title,
                            value: metric.ID,
                            isSelected: orderBy.key === metric.ID,
                          };
                        })}
                        placeholder={'Metric'}
                        onChange={handleSelectOrderBy(idx)}
                      />
                    </Col>
                    <Cols style={{ alignItems: 'center' }} wrap>
                      <Col paddingTop={spacingV2.base} marginRight={spacingV2.base}>
                        <PillSwitcherV2
                          size='smallLong'
                          options={getSimpleSingleOptionSelects(
                            getOrderByPills(orderBy.direction),
                            orderBy.direction,
                          )}
                          onChange={() => handleSwitchOrderByDirection(idx)}
                        />
                      </Col>
                      {width && width > 1350 && (
                        <Col
                          paddingTop={spacingV2.base}
                          marginRight={spacingV2.base}
                          style={{ width: '12rem' }}
                        >
                          <Text small muted>
                            {getOrderByText(orderBy.direction)}
                          </Text>
                        </Col>
                      )}
                      <Col paddingTop={spacingV2.base}>
                        <ButtonV2
                          clearBackground
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                          }}
                          onClick={() => {
                            handleRemoveOrderBy(idx);
                          }}
                        >
                          <IconV2 large name='close' />
                        </ButtonV2>
                      </Col>
                    </Cols>
                  </Cols>
                </Row>
              );
            })}
            {orderBys.length < selectedMetrics.length && (
              <Row style={{ padding: `${spacingV2.base} 0 0 0` }}>
                <TextButton size='small' onClick={handleAddMetric}>
                  + Add Metric
                </TextButton>
              </Row>
            )}
          </Rows>
        )}

        {isOrderByRowExpanded && selectedMetrics.length < 1 && (
          <Row
            displayFlex
            style={{
              border: `1px solid ${publicaColors.lightGray}`,
              borderTop: 'none',
              alignItems: 'center',
              padding: `${spacingV2.base} calc(${spacingV2.huge} + ${spacingV2.large})`,
            }}
          >
            <Text small>Please select some metrics to order by.</Text>
          </Row>
        )}

        <Row
          displayFlex
          smallVerticalPaddingV2
          extraLargeHorizontalPaddingV2
          style={{
            border: `1px solid ${publicaColors.lightGray}`,
            borderTop: 'none',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Cols
            gap={spacingV2.medium}
            style={{
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Col style={{ width: '8.875rem' }}>
              <Cols
                gap={spacingV2.medium}
                style={{
                  alignItems: 'center',
                }}
              >
                <Col>
                  <Switch
                    checked={isLimitRowsActive}
                    onChange={handleLimitRowsActive}
                    color='secondary'
                    size='small'
                  />
                </Col>
                <Col>
                  <Text small bold>
                    Limit Rows
                  </Text>
                </Col>
              </Cols>
            </Col>

            {width && width > 1000 && (
              <Col largeHorizontalPaddingV2>
                <Text small>
                  Use to retrieve only a given number of rows. Comes handy for a quick preview.
                </Text>
              </Col>
            )}

            <Col style={{ marginLeft: 'auto' }}>
              <ButtonV2
                clearBackground
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                }}
                onClick={() => setIsLimitRowsRowExpanded(!isLimitRowsRowExpanded)}
              >
                <IconV2
                  name='caret'
                  color={publicaColors.black}
                  style={{ transform: isOrderByRowExpanded ? 'scaleY(-1)' : 'none' }}
                />
              </ButtonV2>
            </Col>
          </Cols>
        </Row>
        {isLimitRowsRowExpanded && (
          <Row
            style={{
              border: `1px solid ${publicaColors.lightGray}`,
              borderTop: 'none',
              padding: `${spacingV2.base} calc(${spacingV2.huge} + ${spacingV2.large})`,
            }}
          >
            <TextInput
              width='20rem'
              inputSize='small'
              placeholder='Enter Limit'
              initialValue={String(limit)}
              handleOnChange={(input) => setLimit(+input)}
              inputStyle={{ maxWidth: '100%' }}
              type={'number'}
              min={1}
              step={1}
            />
          </Row>
        )}
      </Rows>
    </Rows>
  );
};
